// src/components/students/StudentPage.js
import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  pageStyle,
  colors,
  typography,
  buttons,
  radius,
  shadows,
} from "../../theme";
import { formatDateRus } from "../../utils/formatDateRus";
import { formatPhoneRu } from "../../utils/formatPhoneRu";
import Spinner from "../common/Spinner";

const getLocalDateString = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

function StudentPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const cameFromArchive =
    location.state && location.state.fromArchive === true;

  const [notes, setNotes] = useState([]);
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);

  const [editingName, setEditingName] = useState(false);
  const [editingPhone, setEditingPhone] = useState(false);
  const [nameValue, setNameValue] = useState("");
  const [phoneDigits, setPhoneDigits] = useState("");

  const [nameError, setNameError] = useState(false);
  const [phoneError, setPhoneError] = useState(false);

  const [showDeleteStudent, setShowDeleteStudent] = useState(false);
  const [deleteNameInput, setDeleteNameInput] = useState("");

  const [showDateModal, setShowDateModal] = useState(false);
  const [dateValue, setDateValue] = useState(getLocalDateString);

  const nameInputRef = useRef(null);
  const phoneInputRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const parsePhoneDigitsFromFull = phone => {
    if (!phone) return "";
    let digits = String(phone).replace(/\D/g, "");

    if (digits.startsWith("7") && digits.length >= 11) {
      digits = digits.slice(1);
    } else if (digits.startsWith("8") && digits.length >= 11) {
      digits = digits.slice(1);
    }

    return digits.slice(0, 10);
  };

  const renderPhoneMask = digits => {
    if (!digits) {
      return "+7 (";
    }

    let res = "+7 (";

    if (digits.length <= 3) {
      return res + digits;
    }

    res += digits.slice(0, 3) + ") ";

    if (digits.length <= 6) {
      return res + digits.slice(3);
    }

    res += digits.slice(3, 6) + "-";

    if (digits.length <= 8) {
      return res + digits.slice(6);
    }

    res += digits.slice(6, 8) + "-" + digits.slice(8);
    return res;
  };

  const ensureCaretAfterPhonePrefix = () => {
    if (!phoneInputRef.current) return;
    const input = phoneInputRef.current;
    const prefixLen = 4; // "+7 (" length

    setTimeout(() => {
      try {
        if (
          input.selectionStart < prefixLen ||
          input.selectionEnd < prefixLen
        ) {
          input.setSelectionRange(prefixLen, prefixLen);
        }
      } catch {
        // ignore selection errors
      }
    }, 0);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const load = async () => {
      try {
        const studentRes = await fetch(`/api/students/${id}`);
        if (cancelled) return;

        if (!studentRes.ok) {
          navigate("/");
          return;
        }

        const s = await studentRes.json();

        const notesRes = await fetch(`/api/students/${id}/notes`);
        if (cancelled) return;
        const notesData = await notesRes.json();

        setStudent(s);
        setNameValue(s.name || "");
        setPhoneDigits(parsePhoneDigitsFromFull(s.phone || ""));
        setNotes(notesData || []);
        setNameError(false);
        setPhoneError(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  const saveStudent = async (newNameRaw, newPhoneDigitsRaw) => {
    if (!student) return;

    const safeName = newNameRaw.trim() || "Без имени";
    const digits = newPhoneDigitsRaw || "";
    const formattedPhone = digits ? `+7${digits}` : "";

    const res = await fetch(`/api/students/${student.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: safeName,
        phone: formattedPhone,
        archived: student.archived || 0,
      }),
    });

    const updated = await res.json();

    setStudent(updated);
    setNameValue(updated.name || "");
    setPhoneDigits(parsePhoneDigitsFromFull(updated.phone || ""));
    setNameError(false);
    setPhoneError(false);
  };

  const handleNameBlur = () => {
    const trimmed = nameValue.trim();
    if (!trimmed) {
      setNameError(true);
      if (nameInputRef.current) {
        nameInputRef.current.focus();
      }
      return;
    }

    setNameError(false);
    setEditingName(false);
    saveStudent(nameValue, phoneDigits);
  };

  const handlePhoneBlur = () => {
    const digits = phoneDigits;

    if (digits.length > 0 && digits.length < 10) {
      setPhoneError(true);
      if (phoneInputRef.current) {
        phoneInputRef.current.focus();
        ensureCaretAfterPhonePrefix();
      }
      return;
    }

    setPhoneError(false);
    setEditingPhone(false);
    saveStudent(nameValue, digits);
  };

  const handleNameKeyDown = e => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleNameBlur();
    }
  };

  const handlePhoneKeyDown = e => {
    if (e.key === "Backspace") {
      e.preventDefault();
      if (phoneDigits.length > 0) {
        setPhoneDigits(prev => prev.slice(0, -1));
        setPhoneError(false);
        ensureCaretAfterPhonePrefix();
      }
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      handlePhoneBlur();
    }
  };

  const handlePhoneChange = e => {
    const raw = e.target.value || "";
    let digits = raw.replace(/\D/g, "");

    // Strip leading 7/8 when user types/pastes full number
    if (digits.startsWith("7") || digits.startsWith("8")) {
      if (digits.length > 1) {
        digits = digits.slice(1);
      } else {
        digits = "";
      }
    }

    digits = digits.slice(0, 10);

    setPhoneDigits(digits);
    setPhoneError(false);
    ensureCaretAfterPhonePrefix();
  };

  const handlePhonePaste = e => {
    e.preventDefault();
    const text = e.clipboardData.getData("text") || "";

    let digits = text.replace(/\D/g, "");

    // If pasted text starts with +7, skip that 7 because we already show +7
    if (text.trim().startsWith("+7")) {
      if (digits.startsWith("7")) {
        digits = digits.slice(1);
      }
    } else if (digits.startsWith("7") || digits.startsWith("8")) {
      // Also handle raw 79.. or 89.. patterns
      if (digits.length > 1) {
        digits = digits.slice(1);
      } else {
        digits = "";
      }
    }

    digits = digits.slice(0, 10);

    setPhoneDigits(digits);
    setPhoneError(false);
    ensureCaretAfterPhonePrefix();
  };

  const grouped = {};
  notes.forEach(n => {
    if (!grouped[n.date]) grouped[n.date] = [];
    grouped[n.date].push(n);
  });

  const dates = Object.keys(grouped).sort((a, b) =>
    a < b ? 1 : a > b ? -1 : 0
  );

  const deleteStudent = async () => {
    if (!student) return;
    await fetch(`/api/students/${id}`, {
      method: "DELETE",
    });
    setShowDeleteStudent(false);
    navigate(cameFromArchive ? "/archive" : "/");
  };

  const openNewMeeting = () => {
    setDateValue(getLocalDateString());
    setShowDateModal(true);
  };

  const confirmDate = () => {
    if (!dateValue) return;

    setShowDateModal(false);
    navigate(`/student/${id}/date/${dateValue}`, {
      state: { fromArchive: cameFromArchive },
    });
  };

  const toggleArchive = async () => {
    if (!student) return;
    const newArchived = student.archived ? 0 : 1;

    const res = await fetch(`/api/students/${student.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: nameValue.trim() || "Без имени",
        phone: phoneDigits ? `+7${phoneDigits}` : "",
        archived: newArchived,
      }),
    });

    const updated = await res.json();
    setStudent(updated);
  };

  if (loading) {
    return (
      <div
        style={{
          background: colors.bg,
          minHeight: "100vh",
        }}
      >
        <div style={pageStyle}>
          <button
            onClick={() => navigate(cameFromArchive ? "/archive" : "/")}
            style={
              cameFromArchive
                ? {
                    ...buttons.subtle,
                    fontSize: 24,
                    marginBottom: 18,
                    borderRadius: radius.pill,
                    background: "#fef3c7",
                    color: "#92400e",
                  }
                : {
                    ...buttons.subtle,
                    fontSize: 24,
                    marginBottom: 18,
                    borderRadius: radius.pill,
                  }
            }
          >
            {cameFromArchive ? "← Вернуться в архив" : "← Все ученики"}
          </button>
          <div
            style={{
              paddingTop: 40,
            }}
          >
            <Spinner size={40} />
          </div>
        </div>
      </div>
    );
  }

  if (!student) {
    return null;
  }

  const isArchived = !!student.archived;
  const deleteDisabled =
    deleteNameInput.trim() === "" ||
    deleteNameInput.trim() !== (student.name || "").trim();

  const backButtonLabel = cameFromArchive
    ? "← Вернуться в архив"
    : "← Все ученики";

  const backButtonTarget = cameFromArchive ? "/archive" : "/";

  const backButtonStyle = cameFromArchive
    ? {
        ...buttons.subtle,
        fontSize: 24,
        marginBottom: 18,
        borderRadius: radius.pill,
        background: "#fef3c7",
        color: "#92400e",
      }
    : {
        ...buttons.subtle,
        fontSize: 24,
        marginBottom: 18,
        borderRadius: radius.pill,
      };

  return (
    <div
      style={{
        background: colors.bg,
        minHeight: "100vh",
      }}
    >
      <div style={pageStyle}>
        <button
          onClick={() => {
            window.scrollTo(0, 0);
            navigate(backButtonTarget);
          }}
          style={backButtonStyle}
        >
          {backButtonLabel}
        </button>

        <div
          style={{
            background: colors.card,
            borderRadius: radius.card,
            padding: 24,
            marginBottom: 24,
            boxShadow: shadows.card,
            position: "relative",
            overflow: "hidden",
          }}
        >
          {isArchived && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                background: "#fef3c7",
                color: "#92400e",
                fontSize: 14,
                padding: "4px 10px",
                textAlign: "center",
              }}
            >
              Архив
            </div>
          )}

          <div style={{ marginTop: isArchived ? 18 : 0 }} />

          <div style={{ marginBottom: 14 }}>
            {editingName ? (
              <input
                ref={nameInputRef}
                value={nameValue}
                onChange={e => {
                  setNameError(false);
                  setNameValue(e.target.value);
                }}
                onBlur={handleNameBlur}
                onKeyDown={handleNameKeyDown}
                autoFocus
                style={{
                  fontSize: 34,
                  padding: 10,
                  borderRadius: radius.button,
                  width: "100%",
                  boxSizing: "border-box",
                  border: `1px solid ${colors.borderSoft}`,
                  backgroundColor: nameError ? "#fee2e2" : "white",
                }}
              />
            ) : (
              <h1
                style={{
                  fontSize: 34,
                  margin: 0,
                  marginBottom: 6,
                  cursor: "pointer",
                  color: colors.textMain,
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                }}
                onClick={() => {
                  if (editingPhone && phoneError) {
                    if (phoneInputRef.current) {
                      phoneInputRef.current.focus();
                      ensureCaretAfterPhonePrefix();
                    }
                    return;
                  }
                  setNameError(false);
                  setEditingName(true);
                }}
              >
                {nameValue || "Без имени"}
              </h1>
            )}

            {editingPhone ? (
              <input
                ref={phoneInputRef}
                value={renderPhoneMask(phoneDigits)}
                onChange={handlePhoneChange}
                onPaste={handlePhonePaste}
                onBlur={handlePhoneBlur}
                onKeyDown={handlePhoneKeyDown}
                onFocus={() => {
                  setPhoneError(false);
                  ensureCaretAfterPhonePrefix();
                }}
                onClick={() => {
                  ensureCaretAfterPhonePrefix();
                }}
                autoFocus
                style={{
                  fontSize: typography.bodyLarge,
                  padding: 10,
                  borderRadius: radius.button,
                  width: "100%",
                  boxSizing: "border-box",
                  border: `1px solid ${colors.borderSoft}`,
                  backgroundColor: phoneError ? "#fee2e2" : "white",
                  color:
                    phoneDigits.length === 0
                      ? "#9ca3af"
                      : colors.textMain,
                }}
              />
            ) : (
              <div
                style={{
                  fontSize: typography.bodyLarge,
                  opacity: 0.9,
                  cursor: "pointer",
                  color: student.phone ? colors.textSoft : "#9ca3af",
                }}
                onClick={() => {
                  if (editingName && nameError) {
                    if (nameInputRef.current) {
                      nameInputRef.current.focus();
                    }
                    return;
                  }
                  setPhoneError(false);
                  setEditingPhone(true);
                }}
              >
                {student.phone
                  ? formatPhoneRu(student.phone)
                  : "Добавить телефон"}
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
            <button
              onClick={openNewMeeting}
              style={{
                ...(isArchived ? buttons.secondary : buttons.primary),
                fontSize: 26,
                ...(isArchived
                  ? {
                      background: "#fef3c7",
                      borderColor: "#fef3c7",
                      color: "#92400e",
                    }
                  : {}),
              }}
            >
              Новая встреча
            </button>
          </div>
        </div>

        <div style={{ marginBottom: 32 }}>
          {dates.length === 0 ? (
            <div
              style={{
                fontSize: typography.bodyLarge,
                opacity: 0.75,
                padding: 14,
              }}
            >
              Пока встреч нет
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 14,
              }}
            >
              {dates.map(date => (
                <div
                  key={date}
                  onClick={() => {
                    window.scrollTo(0, 0);
                    navigate(`/student/${id}/date/${date}`);
                  }}
                  style={{
                    padding: 18,
                    background: colors.card,
                    borderRadius: radius.button,
                    fontSize: 32,
                    cursor: "pointer",
                    boxShadow: shadows.card,
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                  }}
                >
                  <span>{formatDateRus(date)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div
          style={{
            marginBottom: 30,
            display: "flex",
            flexDirection: "column",
            gap: 10,
            alignItems: "flex-start",
          }}
        >
          <button
            onClick={toggleArchive}
            style={{
              ...buttons.secondary,
              borderRadius: radius.pill,
              fontSize: 20,
              padding: "10px 18px",
              alignSelf: "flex-start",
              background: "#4b5563",
              borderColor: "#4b5563",
              color: "#f9fafb",
            }}
          >
            {isArchived
              ? "Вернуть ученика из архива"
              : "Архивировать ученика"}
          </button>

          {isArchived && (
            <button
              onClick={() => {
                setDeleteNameInput("");
                setShowDeleteStudent(true);
              }}
              style={{
                ...buttons.dangerSoft,
                borderRadius: radius.pill,
                fontSize: 20,
                padding: "10px 18px",
                alignSelf: "flex-start",
              }}
            >
              Удалить ученика
            </button>
          )}
        </div>
      </div>

      {showDateModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.55)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: 16,
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: colors.card,
              padding: 24,
              borderRadius: radius.card,
              width: "90%",
              maxWidth: 380,
              boxSizing: "border-box",
              boxShadow: "0 24px 60px rgba(15, 23, 42, 0.5)",
              fontSize: typography.body,
              color: colors.textMain,
            }}
          >
            <h2
              style={{
                marginTop: 0,
                marginBottom: 16,
                fontSize: typography.sectionTitle,
              }}
            >
              Дата встречи
            </h2>

            <input
              type="date"
              value={dateValue}
              onChange={e => setDateValue(e.target.value)}
              style={{
                width: "100%",
                fontSize: typography.bodyLarge,
                padding: 12,
                borderRadius: radius.button,
                border: `1px solid ${colors.borderSoft}`,
                boxSizing: "border-box",
                marginBottom: 20,
              }}
            />

            <div
              style={{
                display: "flex",
                gap: 12,
              }}
            >
              <button
                onClick={confirmDate}
                style={{
                  ...buttons.primary,
                  flex: 1,
                  fontSize: 22,
                  padding: "12px 0",
                }}
              >
                Открыть
              </button>
              <button
                onClick={() => setShowDateModal(false)}
                style={{
                  ...buttons.secondary,
                  flex: 1,
                  fontSize: 22,
                  padding: "12px 0",
                }}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteStudent && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.55)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: 16,
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: colors.card,
              padding: 24,
              borderRadius: radius.card,
              width: "90%",
              maxWidth: 380,
              boxSizing: "border-box",
              boxShadow: "0 24px 60px rgba(15, 23, 42, 0.5)",
              fontSize: typography.body,
              color: colors.textMain,
            }}
          >
            <h2
              style={{
                marginTop: 0,
                marginBottom: 10,
                fontSize: typography.sectionTitle,
              }}
            >
              Удалить ученика и все заметки
            </h2>
            <p
              style={{
                marginBottom: 12,
                fontSize: typography.body,
              }}
            >
              Чтобы удалить, введи имя ученика
            </p>
            <p
              style={{
                marginBottom: 8,
                fontSize: typography.bodySmall,
                color: colors.textSoft,
                wordBreak: "break-word",
                overflowWrap: "break-word",
              }}
            >
              Имя: {student.name}
            </p>
            <input
              value={deleteNameInput}
              onChange={e => setDeleteNameInput(e.target.value)}
              placeholder="Имя ученика"
              style={{
                width: "100%",
                fontSize: typography.bodyLarge,
                padding: 10,
                borderRadius: radius.button,
                border: `1px solid ${colors.borderSoft}`,
                boxSizing: "border-box",
                marginBottom: 16,
              }}
            />
            <div
              style={{
                display: "flex",
                gap: 12,
              }}
            >
              <button
                onClick={deleteStudent}
                disabled={deleteDisabled}
                style={{
                  ...buttons.danger,
                  flex: 1,
                  fontSize: 22,
                  padding: "12px 0",
                  opacity: deleteDisabled ? 0.5 : 1,
                  pointerEvents: deleteDisabled ? "none" : "auto",
                }}
              >
                Удалить
              </button>
              <button
                onClick={() => setShowDeleteStudent(false)}
                style={{
                  ...buttons.secondary,
                  flex: 1,
                  fontSize: 22,
                  padding: "12px 0",
                }}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StudentPage;
